import type { TextQuestion } from '@shared/domain/form';
import type { LegalCheckFlag } from '../../legal-check/types';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { TextareaAutosize } from '@mui/material';
import CustomTooltip from '@shared/components/Tooltip';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import LegalCheckPanel from '../../legal-check/LegalCheckPanel';

export interface LegalCheckBindings {
  flags: LegalCheckFlag[];
  inFlight: boolean;
  // Fires on every keystroke; the hook debounces internally so the AI call
  // only runs once after the user stops typing.
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onDismiss: (flagIndex: number) => void;
}

interface ReviewTextAreaProps {
  question: TextQuestion;
  formFields: Record<string, any>;
  handleFormFieldChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  errors: Record<string, string>;
  includeTextLimitValidation?: boolean;
  helperText?: string;
  index?: number;
  short?: boolean;
  legalCheck?: LegalCheckBindings;
  isConditional?: boolean;
}

const CHARACTER_MIN_LIMIT = 50;

// Conditional questions should feel like they're settling into place
// rather than popping. Slow ease-out, small translate, no scale jump —
// the SCSS glow ring carries the "look at me" job. Pairs with the
// 1.6s glow on .review-question--conditional so the motion finishes
// just as the ring fades.
const CONDITIONAL_ENTRANCE = {
  initial: { opacity: 0, y: -6 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
};

const DEFAULT_ENTRANCE = (index: number) => ({
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.25, ease: 'easeOut' as const, delay: index * 0.04 },
});

const ReviewTextArea: React.FC<ReviewTextAreaProps> = ({
  question,
  formFields,
  handleFormFieldChange,
  errors,
  includeTextLimitValidation,
  helperText,
  index = 0,
  legalCheck,
  isConditional = false,
}) => {
  const value = formFields[question.name] || '';
  const [remainingCharacters, setRemainingCharacters] = useState(Math.max(CHARACTER_MIN_LIMIT - value.length, 0));

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = event.target.value;
    setRemainingCharacters(Math.max(CHARACTER_MIN_LIMIT - text.length, 0));
    handleFormFieldChange(event);
    legalCheck?.onChange(event);
  };

  const isBelowMinimum = (value.length || 0) < CHARACTER_MIN_LIMIT;
  const hasError = !!errors[question.name];
  const helperId = helperText ? `${question.name}-desc` : undefined;

  const motionProps = isConditional ? CONDITIONAL_ENTRANCE : DEFAULT_ENTRANCE(index);
  const className = [
    'review-question',
    isConditional && 'review-question--conditional',
    hasError && 'review-question--has-error',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <motion.section className={className} data-testid={question.name} {...motionProps}>
      <div className="review-question__prompt-row">
        <h3 id={`${question.name}-label`} className="review-question__prompt">
          <span>{question.question}</span>
          {question.required && <span className="review-question__required">Required</span>}
          {question.tooltipText && <CustomTooltip title={question.tooltipText} />}
        </h3>
      </div>

      {/*
        Always render the helper slot so the subgrid in .review-text-grid
        keeps prompt / helper / answer rows aligned across columns even when
        only some questions have helper text. An empty <p> with no content
        collapses visually (line-height-based min via SCSS) but reserves the
        row so neighbours can pad to match.
      */}
      <p
        id={helperId}
        className={`review-question__helper${helperText ? '' : ' review-question__helper--empty'}`}
        aria-hidden={!helperText}
      >
        {helperText ?? ''}
      </p>

      <div className="review-question__answer">
        <TextareaAutosize
          aria-labelledby={`${question.name}-label`}
          aria-invalid={hasError}
          aria-describedby={helperId}
          onChange={handleTextChange}
          name={question.name}
          value={value}
          minRows={4}
          required={question.required}
          className={`review-textarea${hasError ? ' review-textarea--error' : ''}`}
        />

        {includeTextLimitValidation && (
          <span
            className={`review-question__counter${isBelowMinimum ? '' : ' review-question__counter--met'}`}
            aria-live="polite"
          >
            {isBelowMinimum ? `${remainingCharacters} more characters needed` : 'Minimum met ✓'}
          </span>
        )}

        {hasError && (
          <div className="review-error" role="alert">
            <WarningAmberIcon />
            <span>{errors[question.name]}</span>
          </div>
        )}

        {legalCheck && (
          <LegalCheckPanel
            flags={legalCheck.flags}
            inFlight={legalCheck.inFlight}
            currentValue={value}
            onDismiss={legalCheck.onDismiss}
          />
        )}
      </div>
    </motion.section>
  );
};

export default ReviewTextArea;
